// Adapter for the agent files history (Phase 1 feature).
// Plugs into GenericHistoryDrawer to provide the same behavior as the original
// HistoryDrawer.tsx, but through the generalized adapter interface.

import yaml from "js-yaml";
import { useAgentFileVersions, useRestoreVersion } from "@/hooks/useAgentFileVersions";
import type { AgentFileVersion, Frontmatter } from "@/types/agent-skills";
import type { VersionAdapter, DiffContent } from "@/components/history/VersionAdapter";

export interface AgentFileCurrentState {
  frontmatter: Frontmatter;
  body: string;
}

function versionToText(frontmatter: Frontmatter, body: string): string {
  const yamlStr = yaml.dump(frontmatter, { lineWidth: -1, quotingType: '"', forceQuotes: false });
  return `---\n${yamlStr}---\n${body}`;
}

export const agentFileVersionAdapter: VersionAdapter<AgentFileVersion, AgentFileCurrentState> = {
  useVersions(entityId: string) {
    return useAgentFileVersions(entityId);
  },

  useRestore() {
    const restore = useRestoreVersion();
    return {
      ...restore,
      mutateAsync: async ({
        entityId,
        version,
        expectedVersion,
        note,
      }: {
        entityId: string;
        version: AgentFileVersion;
        expectedVersion: number;
        note?: string;
      }) => {
        return restore.mutateAsync({ fileId: entityId, version, expectedVersion, note });
      },
    } as ReturnType<typeof useRestoreVersion>;
  },

  formatVersion(version: AgentFileVersion, currentState: AgentFileCurrentState): DiffContent {
    return {
      label: `v${version.version_number}`,
      before: versionToText(version.frontmatter, version.body),
      after: versionToText(currentState.frontmatter, currentState.body),
    };
  },
};
